import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Pagination } from '@/components/shared/Pagination';
import { Eye } from 'lucide-react';
import { formatCurrency, formatDate, formatCNPJ } from '@/lib/utils/formatters';
import { calcularTotalPaginas } from './documentos-helpers';
import { ibsTotalDaNota, tipoEFinalidadeDaNota } from './ibs-e-tipo-da-nota';
import {
  CLASSE_DO_ALINHAMENTO,
  colunasDaLista,
  creditoDisponivelDaNota,
  marcasDaNota,
  situacaoDaNota,
  valorDaNota,
} from './colunas-da-lista';
import type { Documento } from '@/services/Documentos';

interface DocumentosTableProps {
  documentos: Documento[];
  onViewDocument?: (doc: Documento) => void;
  tipo: 'entradas' | 'saidas';
  totalItens: number;
  paginaAtual: number;
  itensPorPagina: number;
  onPaginaChange: (pagina: number) => void;
  onItensPorPaginaChange?: (itens: number) => void;
}

export function DocumentosTable({
  documentos,
  onViewDocument,
  tipo,
  totalItens,
  paginaAtual,
  itensPorPagina,
  onPaginaChange,
  onItensPorPaginaChange,
}: DocumentosTableProps) {
  const totalPaginas = calcularTotalPaginas(totalItens, itensPorPagina);
  const corDoImposto = tipo === 'saidas' ? 'text-red-600 font-semibold' : '';

  if (documentos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">Nenhum documento encontrado</div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            {colunasDaLista(tipo).map(coluna => (
              <TableHead key={coluna.titulo} className={CLASSE_DO_ALINHAMENTO[coluna.alinhamento]}>
                {coluna.titulo}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {documentos.map(doc => {
            const vIbsTotal = ibsTotalDaNota(doc);
            const credito = creditoDisponivelDaNota(doc);

            return (
              <TableRow key={doc.ChaveAcessoDOC}>
                <TableCell className="font-mono text-xs">
                  {formatCNPJ(doc.CNPJ_Contribuinte)}
                </TableCell>
                <TableCell className="font-mono text-sm">{doc.Numero}</TableCell>
                <TableCell className="text-sm">{formatDate(doc.DataEmissao)}</TableCell>
                <TableCell className="text-sm max-w-[200px] truncate">{doc.RazaoSocial}</TableCell>
                <TableCell className="font-mono text-xs">{formatCNPJ(doc.CNPJ)}</TableCell>
                <TableCell className="text-sm">
                  {tipoEFinalidadeDaNota(doc)}
                  {marcasDaNota(doc).map(marca => (
                    <Badge key={marca} variant="outline" className="ml-2 text-xs font-normal">
                      {marca}
                    </Badge>
                  ))}
                </TableCell>
                <TableCell className="text-sm">{situacaoDaNota(doc)}</TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(parseFloat(doc.vNF) || 0)}
                </TableCell>
                <TableCell className="text-right text-sm">
                  <div className={corDoImposto}>{formatCurrency(valorDaNota(doc.vIBSUF))}</div>
                </TableCell>
                <TableCell className="text-right text-sm">
                  <div className={corDoImposto}>{formatCurrency(valorDaNota(doc.vIBSMun))}</div>
                </TableCell>
                <TableCell className="text-right text-sm">
                  <div className={corDoImposto}>{formatCurrency(vIbsTotal)}</div>
                  <div className="text-xs text-muted-foreground">
                    {vIbsTotal > 0 ? '0.10%' : '-'}
                  </div>
                </TableCell>
                <TableCell className="text-right text-sm">
                  <div className={corDoImposto}>{formatCurrency(parseFloat(doc.vCBS) || 0)}</div>
                  <div className="text-xs text-muted-foreground">
                    {parseFloat(doc.vCBS) > 0 ? '0.90%' : '-'}
                  </div>
                </TableCell>
                {tipo === 'entradas' && (
                  <TableCell className="text-right text-sm">
                    {credito ? (
                      <>
                        <div>IBS {formatCurrency(credito.ibs)}</div>
                        <div>CBS {formatCurrency(credito.cbs)}</div>
                      </>
                    ) : (
                      <span
                        className="text-muted-foreground"
                        title="Sem crédito calculado: o crédito é da NF-e liberada pelo Recebimento"
                      >
                        -
                      </span>
                    )}
                  </TableCell>
                )}
                <TableCell className="text-right text-sm text-muted-foreground">-</TableCell>
                <TableCell className="text-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="sm" onClick={() => onViewDocument?.(doc)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="start">
                      <p>Detalhes da Nota</p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Paginação */}
      <div className="flex items-center py-4">
        <Pagination
          currentPage={paginaAtual}
          totalPages={totalPaginas}
          onPageChange={onPaginaChange}
          itensPorPagina={itensPorPagina}
          onItensPorPaginaChange={onItensPorPaginaChange}
        />
      </div>
    </div>
  );
}
